import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LightningIcon } from "@phosphor-icons/react";
import "./AiTalentDriveBanner.css";

/*
 * AI Talent Drive banner (G1 · floating glass)
 *
 * Rendered by App.tsx right below the fixed site header on every page except
 * the drive page itself. Everything you are likely to change lives in CONFIG.
 * The banner hides itself automatically once registration closes. Dismissing
 * it only hides it for the current page view: it comes back on every reload
 * and route change.
 */
const CONFIG = {
  // Registration deadline, with the IST offset. The backend's
  // HIRING_CIRCUIT_REGISTRATION_CLOSES_AT (served by /hiring-circuit/config/)
  // replaces this once loaded; keep it in step as the fallback.
  closesAt: "2026-10-30T23:59:59+05:30",
  candidateUrl: "/ai-talent-drive",
  companyUrl: "/ai-talent-drive",
  apiBase: "https://interview.babblebots.ai",
};

function ArrowIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );
}

const scrollTo = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

type Props = {
  /* "hero" is the landing graphic at the top of the drive page: centred, no
     dismiss button, and its CTAs scroll within the page instead of linking. */
  variant?: "banner" | "hero";
  /* Who the hero speaks to: the candidate drive page or the company sign-up
     page. Only the headline, subline and CTAs change. */
  audience?: "candidates" | "companies";
};

export default function AiTalentDriveBanner({
  variant = "banner",
  audience = "candidates",
}: Props) {
  const isHero = variant === "hero";
  const forCompanies = audience === "companies";
  const [closesAt, setClosesAt] = useState(CONFIG.closesAt);
  const target = Date.parse(closesAt);
  const [msLeft, setMsLeft] = useState(() => target - Date.now());
  const [dismissed, setDismissed] = useState(false);

  // The backend owns the deadline; the built-in one only covers the moment
  // before the config has loaded (or when it can't be reached).
  useEffect(() => {
    let cancelled = false;
    fetch(`${CONFIG.apiBase}/hiring-circuit/config/`)
      .then((res) => (res.ok ? res.json() : null))
      .then((config) => {
        if (!cancelled && config?.closes_at) setClosesAt(config.closes_at);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (dismissed) return undefined;
    setMsLeft(target - Date.now());
    const id = window.setInterval(() => setMsLeft(target - Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [target, dismissed]);

  if (dismissed || (!isHero && msLeft <= 0)) return null;

  const dismiss = () => setDismissed(true);

  return (
    <div className={`aitd${isHero ? " aitd--hero" : ""}`}>
      <div className="aitd__blob aitd__blob--indigo" aria-hidden="true" />
      <div className="aitd__blob aitd__blob--lime" aria-hidden="true" />
      <div className="aitd__blob aitd__blob--cyan" aria-hidden="true" />

      <section className="aitd__card" aria-labelledby="aitd-title">
        <div className="aitd__shine" aria-hidden="true" />

        <div className="aitd__copy">
          {isHero ? (
            <div className="aitd__eyebrow">
              <span className="aitd__live">
                <span className="aitd__dot" aria-hidden="true" />
                <span className="aitd__live-text">
                  OCT - NOV 2026 <span className="aitd__pipe" aria-hidden="true">|</span>{" "}
                  NATIONWIDE VIRTUAL DRIVE
                </span>
              </span>
            </div>
          ) : (
          <div className="aitd__eyebrow">
            <span className="aitd__live">
              <span className="aitd__dot" aria-hidden="true" />
              LIVE · Oct - Nov 2026
            </span>
            <span className="aitd__virtual">100% VIRTUAL</span>
          </div>
          )}
          {isHero ? (
            <h1 id="aitd-title" className="aitd__title">
              {forCompanies && "Hire from "}
              India&rsquo;s largest <span className="aitd__lime">AI Talent Drive</span>
            </h1>
          ) : (
            <h2 id="aitd-title" className="aitd__title">
              Welcome to India&rsquo;s largest{" "}
              <span className="aitd__lime">AI Talent Drive</span>
            </h2>
          )}
          {isHero && forCompanies ? (
            /* One line on wide screens; stacks into two lines (no divider)
               when it can't fit, with the bolt kept on the first line. */
            <p className="aitd__tagline aitd__promise aitd__promise--co">
              <span>
                <LightningIcon className="aitd__bolt" weight="fill" aria-hidden="true" />{" "}
                1000s of AI-Interviewed and Assessed Candidates
              </span>{" "}
              <span className="aitd__pipe" aria-hidden="true">|</span>{" "}
              <span className="aitd__lime">Free for Hiring Companies</span>
            </p>
          ) : isHero ? (
            /* Same promise as the "One Interview" block further down the
               page, so it gets the same weight: display face, bolt icon. */
            <p className="aitd__tagline aitd__promise">
              <LightningIcon className="aitd__bolt" weight="fill" aria-hidden="true" />
              <span className="aitd__promise-a">One Interview</span>{" "}
              <span className="aitd__pipe" aria-hidden="true">|</span>{" "}
              <span>
                Auto-apply to <span className="aitd__lime">50+ Companies</span> for{" "}
                <span className="aitd__nowrap">AI-Jobs</span>
              </span>
            </p>
          ) : (
          <p className="aitd__tagline">
            One Interview <span className="aitd__pipe" aria-hidden="true">|</span>{" "}
            Auto-apply to 50+ Companies for AI-Jobs
          </p>
          )}
          {!isHero && <p className="aitd__when">Oct - Nov 2026</p>}
        </div>

        {isHero ? (
          <div className="aitd__ctas">
            <button
              type="button"
              className="aitd__btn aitd__btn--primary"
              onClick={() => scrollTo(forCompanies ? "signup" : "register")}
            >
              <span>{forCompanies ? "Sign Up Today" : "Register Today"}</span>
              <ArrowIcon />
            </button>
            {/* The company page's How it works section sits right below the
                hero, so it only needs the sign-up button */}
            {!forCompanies && (
              <button
                type="button"
                className="aitd__btn aitd__btn--ghost"
                onClick={() => scrollTo("how")}
              >
                <span>How It Works</span>
              </button>
            )}
          </div>
        ) : (
        <div className="aitd__ctas">
          <Link className="aitd__btn aitd__btn--primary" to={CONFIG.candidateUrl}>
            <span>Free Candidate registration</span>
            <ArrowIcon />
          </Link>
          <Link className="aitd__btn aitd__btn--ghost" to={CONFIG.companyUrl}>
            <span>Free Company Signup</span>
            <ArrowIcon />
          </Link>
        </div>
        )}

        {!isHero && (
        <button
          type="button"
          className="aitd__close"
          aria-label="Dismiss AI Talent Drive banner"
          onClick={dismiss}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12" />
            <path d="M18 6L6 18" />
          </svg>
        </button>
        )}
      </section>
    </div>
  );
}
